import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { format, parseISO } from "date-fns";
import { toast } from "sonner";
import { API } from "@/lib/constants";
import { Calendar } from "@/components/ui/calendar";
import { Loader2, CheckCircle2, CalendarClock, XCircle } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function ReschedulePage() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [state, setState] = useState({ loading: true, error: "", booking: null, availability: null, cancellation: null });
  const [date, setDate] = useState(undefined);
  const [slot, setSlot] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelled, setCancelled] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/reschedule/${token}`);
        setState({ loading: false, error: "", booking: res.data.booking, availability: res.data.availability, cancellation: res.data.cancellation });
      } catch (e) {
        setState({ loading: false, error: e.response?.data?.detail || "We couldn't find that booking.", booking: null, availability: null, cancellation: null });
      }
    })();
  }, [token]);

  if (state.loading) {
    return (
      <main className="min-h-screen bg-[#EBEBFA] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#1A243B]" />
      </main>
    );
  }

  const { booking, availability, cancellation } = state;
  const svc = booking?.service;
  const serviceSlots = availability?.service_slots?.[svc]?.time_slots || [];
  const dateKey = date ? format(date, "yyyy-MM-dd") : "";
  const fullToday = ((availability?.full_slots?.[dateKey]) || {})[svc] || [];
  const isBlocked = (d) => (availability?.blocked_dates || []).includes(format(d, "yyyy-MM-dd"));

  const submit = async () => {
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/reschedule/${token}`, { date: dateKey, time_slot: slot });
      setDone(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not reschedule. Please try another time.");
    } finally {
      setSubmitting(false);
    }
  };

  const doCancel = async () => {
    setCancelling(true);
    try {
      const res = await axios.post(`${API}/reschedule/${token}/cancel`);
      setCancelled(res.data);
      setCancelOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not cancel. Please call 503-449-8323.");
      setCancelOpen(false);
    } finally {
      setCancelling(false);
    }
  };

  return (
    <main className="min-h-screen bg-[#EBEBFA] flex items-center justify-center px-6 py-16" data-testid="reschedule-page">
      <div className="bg-white rounded-3xl shadow-sm p-8 md:p-12 max-w-2xl w-full">
        {state.error ? (
          <div className="text-center">
            <h1 className="font-display text-2xl text-[#1A243B] mb-3" data-testid="reschedule-error">{state.error}</h1>
            <p className="text-[#2D3748] text-sm mb-6">Check the link in your confirmation email, or call 503-449-8323.</p>
            <Link to="/" data-testid="reschedule-home" className="cta-pill inline-block bg-[#FFC107] text-[#1A243B] font-bold px-8 py-3 rounded-full">Back to homepage</Link>
          </div>
        ) : done ? (
          <div className="text-center">
            <span className="w-16 h-16 rounded-full bg-[#FFC107] flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-8 h-8 text-[#1A243B]" />
            </span>
            <h1 className="font-display text-3xl text-[#1A243B] mb-3" data-testid="reschedule-done-heading">You're rescheduled</h1>
            <p className="text-[#2D3748] text-base mb-2" data-testid="reschedule-done-detail">
              {booking.service_name} — {format(parseISO(done.date), "EEEE, MMMM d, yyyy")} at {done.time_slot}.
            </p>
            <p className="text-[#2D3748] text-base mb-8">A confirmation email is on its way to you.</p>
            <Link to="/" data-testid="reschedule-home" className="cta-pill inline-block bg-[#FFC107] text-[#1A243B] font-bold px-8 py-3 rounded-full">Back to homepage</Link>
          </div>
        ) : cancelled ? (
          <div className="text-center">
            <span className="w-16 h-16 rounded-full bg-[#EBEBFA] flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-8 h-8 text-[#1A243B]" />
            </span>
            <h1 className="font-display text-3xl text-[#1A243B] mb-3" data-testid="cancel-done-heading">Booking cancelled</h1>
            <p className="text-[#2D3748] text-base mb-8" data-testid="cancel-done-detail">
              {cancelled.refunded
                ? "Your payment has been refunded in full — it may take a few days to reach your card. A confirmation email is on its way."
                : "Your booking is cancelled. A confirmation email is on its way to you."}
            </p>
            <Link to="/" data-testid="reschedule-home" className="cta-pill inline-block bg-[#FFC107] text-[#1A243B] font-bold px-8 py-3 rounded-full">Back to homepage</Link>
          </div>
        ) : booking.status !== "confirmed" ? (
          <div className="text-center">
            <h1 className="font-display text-2xl text-[#1A243B] mb-3" data-testid="reschedule-error">This booking can't be rescheduled</h1>
            <p className="text-[#2D3748] text-sm mb-6">It may have been cancelled. Call 503-449-8323 and Julie will help.</p>
            <Link to="/" data-testid="reschedule-home" className="cta-pill inline-block bg-[#FFC107] text-[#1A243B] font-bold px-8 py-3 rounded-full">Back to homepage</Link>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-3 mb-2">
              <span className="w-11 h-11 rounded-full bg-[#EBEBFA] flex items-center justify-center">
                <CalendarClock className="w-5 h-5 text-[#1A243B]" />
              </span>
              <h1 className="font-display text-2xl md:text-3xl text-[#1A243B]">Reschedule your booking</h1>
            </div>
            <p className="text-[#2D3748] text-base mb-8" data-testid="reschedule-current">
              {booking.service_name} for {booking.name} — currently {format(parseISO(booking.date), "EEEE, MMMM d, yyyy")}
              {booking.time_slot ? ` at ${booking.time_slot}` : ""}.
            </p>

            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-[#EBEBFA] rounded-2xl p-5 w-fit" data-testid="reschedule-calendar-wrap">
                <Calendar
                  mode="single"
                  selected={date}
                  onSelect={(d) => { setDate(d); setSlot(""); }}
                  disabled={(d) => d <= new Date(new Date().setHours(23, 59, 59, 999)) || isBlocked(d)}
                  data-testid="reschedule-calendar"
                  className="rounded-xl bg-white"
                />
              </div>
              <div>
                {date && (
                  <>
                    <p className="font-display text-lg text-[#1A243B] mb-3" data-testid="reschedule-date-label">
                      {format(date, "EEEE, MMMM d, yyyy")}
                    </p>
                    <p className="text-sm font-bold text-[#1A243B] mb-3">Pick a new time</p>
                    <div className="flex flex-wrap gap-2" data-testid="reschedule-slot-picker">
                      {serviceSlots.map((s) => {
                        const full = fullToday.includes(s);
                        return (
                          <button
                            key={s}
                            type="button"
                            data-testid={`reschedule-slot-${s.replace(/[\s:]/g, "-").toLowerCase()}`}
                            disabled={full}
                            onClick={() => setSlot(s)}
                            className={`px-4 py-2 rounded-full border font-semibold text-sm transition-colors ${
                              full
                                ? "bg-[#1A243B]/5 text-[#1A243B]/35 border-transparent line-through cursor-not-allowed"
                                : slot === s
                                  ? "bg-[#1A243B] text-[#FFC107] border-[#1A243B]"
                                  : "bg-white text-[#1A243B] border-[#1A243B]/20 hover:border-[#1A243B]/50"
                            }`}
                          >
                            {s}
                          </button>
                        );
                      })}
                    </div>
                    <button
                      data-testid="reschedule-confirm-btn"
                      disabled={!slot || submitting}
                      onClick={submit}
                      className="cta-pill mt-8 w-full bg-[#FFC107] text-[#1A243B] font-bold py-3.5 rounded-full flex items-center justify-center gap-2 disabled:opacity-40 disabled:pointer-events-none"
                    >
                      {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                      Confirm new time
                    </button>
                  </>
                )}
                {!date && <p className="text-[#2D3748]/60 text-sm mt-2">Pick a new date on the calendar.</p>}
              </div>
            </div>

            <div className="border-t border-[#1A243B]/10 mt-10 pt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <p className="text-sm text-[#2D3748]/70 max-w-sm" data-testid="cancel-policy-text">
                {cancellation?.refundable_now
                  ? `Changed your mind? You can cancel for a full refund up to ${cancellation.refund_window_hours} hours before your session.`
                  : `This session is inside the ${cancellation?.refund_window_hours ?? 48}-hour refund window, so cancelling won't refund the payment.`}
              </p>
              <button
                data-testid="parent-cancel-btn"
                onClick={() => setCancelOpen(true)}
                className="text-sm font-bold text-red-700 hover:text-red-900 border border-red-200 hover:border-red-400 rounded-full px-6 py-2.5 transition-colors shrink-0"
              >
                Cancel this booking
              </button>
            </div>
          </>
        )}
      </div>

      <AlertDialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <AlertDialogContent data-testid="parent-cancel-dialog" className="rounded-3xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-[#1A243B]">Cancel this booking?</AlertDialogTitle>
            <AlertDialogDescription>
              {booking && cancellation && (
                cancellation.refundable_now
                  ? `Your ${booking.service_name} on ${format(parseISO(booking.date), "MMM d, yyyy")} will be cancelled and you'll be refunded $${(booking.total_cents / 100).toFixed(2)} in full.`
                  : `Your ${booking.service_name} on ${format(parseISO(booking.date), "MMM d, yyyy")} will be cancelled. This is inside the ${cancellation.refund_window_hours}-hour refund window, so the payment won't be refunded.`
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="parent-cancel-no" className="rounded-full">Keep my booking</AlertDialogCancel>
            <AlertDialogAction
              data-testid="parent-cancel-yes"
              onClick={doCancel}
              disabled={cancelling}
              className="rounded-full bg-red-600 text-white hover:bg-red-700"
            >
              {cancelling ? "Cancelling…" : "Yes, cancel it"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </main>
  );
}
